import React from 'react';
import { PhoneIcon, MapPinIcon, ClockIcon, WrenchIcon, TruckIcon, ShoppingCartIcon } from '@heroicons/react/24/outline';

function App() {
  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-blue-600 text-white">
        <div className="container mx-auto px-4 py-6">
          <h1 className="text-4xl font-bold">Дуслык</h1>
          <p className="text-xl mt-2">Шиномонтаж, автосервис и магазин запчастей для грузовых автомобилей</p>
        </div>
      </header>

      {/* Main Content */}
      <main className="container mx-auto px-4 py-12">
        {/* Hero Section with Image */}
        <section className="mb-16">
          <div className="relative h-[400px] rounded-lg overflow-hidden">
            <img 
              src="/images/hero.png" 
              alt="Шиномонтаж Дуслык" 
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-black bg-opacity-40 flex items-center justify-center">
              <div className="text-center text-white">
                <h2 className="text-4xl font-bold mb-4">Профессиональный сервис</h2>
                <p className="text-xl">Шиномонтаж, ремонт и запчасти для грузовых автомобилей</p>
              </div>
            </div>
          </div>
        </section>

        {/* Services Section */}
        <section className="mb-16">
          <h2 className="text-3xl font-bold text-blue-600 mb-6">Наши услуги</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="bg-white p-6 rounded-lg shadow-md">
              <WrenchIcon className="h-12 w-12 text-blue-500 mb-4" />
              <h3 className="text-xl font-semibold mb-2">Шиномонтаж</h3>
              <ul className="text-gray-600 space-y-2">
                <li>• Монтаж/демонтаж шин</li>
                <li>• Балансировка</li>
                <li>• Вулканизация</li>
                <li>• Ремонт проколов</li>
                <li>• Ремонт порезов</li>
                <li>• Сезонное хранение</li>
                <li>• Ремонт грузовых шин</li>
              </ul>
            </div>
            <div className="bg-white p-6 rounded-lg shadow-md">
              <TruckIcon className="h-12 w-12 text-blue-500 mb-4" />
              <h3 className="text-xl font-semibold mb-2">Автосервис</h3>
              <ul className="text-gray-600 space-y-2">
                <li>• Диагностика</li>
                <li>• Ремонт ходовой части</li>
                <li>• Ремонт тормозной системы</li>
                <li>• Замена масла</li>
                <li>• Ремонт двигателя</li>
                <li>• Ремонт КПП</li>
                <li>• Ремонт редуктора</li>
              </ul>
            </div>
            <div className="bg-white p-6 rounded-lg shadow-md">
              <ShoppingCartIcon className="h-12 w-12 text-blue-500 mb-4" />
              <h3 className="text-xl font-semibold mb-2">Магазин запчастей</h3>
              <ul className="text-gray-600 space-y-2">
                <li>• Шины и диски</li>
                <li>• Запчасти для грузовиков</li>
                <li>• Масла и фильтры</li>
                <li>• Тормозные колодки</li>
                <li>• Подшипники</li>
                <li>• Аккумуляторы</li>
                <li>• Ремни и комплектующие</li>
              </ul>
            </div>
          </div>
        </section>

        {/* About Section */}
        <section className="mb-16">
          <h2 className="text-3xl font-bold text-blue-600 mb-6">О нас</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div>
              <p className="text-lg text-gray-700 mb-4">
                Автосервис "Дуслык" специализируется на обслуживании грузовых автомобилей. 
                Мы предоставляем полный спектр услуг по ремонту и обслуживанию грузового транспорта.
              </p>
              <p className="text-lg text-gray-700">
                Наш сервис оснащен современным оборудованием, а опытные мастера 
                выполняют все работы качественно и в кратчайшие сроки. Мы работаем с грузовыми автомобилями всех марок.
              </p>
            </div>
            <div className="rounded-lg overflow-hidden">
              <img 
                src="/images/about.png" 
                alt="О компании Дуслык" 
                className="w-full h-full object-cover"
              />
            </div>
          </div>
        </section>

        {/* Shop Section */}
        <section className="mb-16">
          <h2 className="text-3xl font-bold text-blue-600 mb-6">Наш магазин запчастей</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div className="rounded-lg overflow-hidden">
              <img 
                src="/images/shop.png" 
                alt="Магазин запчастей Дуслык" 
                className="w-full h-full object-cover"
              />
            </div>
            <div>
              <p className="text-lg text-gray-700 mb-4">
                В нашем магазине запчастей вы найдете все необходимое для вашего грузового автомобиля:
              </p>
              <ul className="text-lg text-gray-700 space-y-2">
                <li>• Широкий ассортимент запчастей для грузовиков</li>
                <li>• Оригинальные и качественные аналоги</li>
                <li>• Масла и технические жидкости</li>
                <li>• Фильтры и расходные материалы</li>
                <li>• Шины и диски</li>
                <li>• Аккумуляторы и электрооборудование</li>
                <li>• Тормозные колодки и комплектующие</li>
              </ul>
            </div>
          </div>
        </section>

        {/* Contact Information */}
        <section className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-16">
          <div className="bg-white p-6 rounded-lg shadow-md">
            <PhoneIcon className="h-8 w-8 text-blue-500 mb-4" />
            <h3 className="text-xl font-semibold mb-2">Телефон</h3>
            <p className="text-gray-600">+7 (927) 638-30-08</p>
          </div>
          
          <div className="bg-white p-6 rounded-lg shadow-md">
            <MapPinIcon className="h-8 w-8 text-blue-500 mb-4" />
            <h3 className="text-xl font-semibold mb-2">Адрес</h3>
            <p className="text-gray-600">Республика Башкортостан, Дюртюлинский район, Такарликовский сельсовет, 118-й км автомагистрали Москва-Уфа, 1А</p>
          </div>
          
          <div className="bg-white p-6 rounded-lg shadow-md">
            <ClockIcon className="h-8 w-8 text-blue-500 mb-4" />
            <h3 className="text-xl font-semibold mb-2">Режим работы</h3>
            <div className="text-gray-600">
              <p className="mb-2">Магазин запчастей:</p>
              <p>Ежедневно 09:00-20:00</p>
            </div>
            <div className="text-gray-600 mt-2">
              <p className="mb-2">Шиномонтаж:</p>
              <p>Ежедневно 09:00-19:00</p>
            </div>
            <div className="text-gray-600 mt-2">
              <p className="mb-2">Автосервис:</p>
              <p>Круглосуточно 24/7</p>
            </div>
          </div>
        </section>

        {/* Map Section */}
        <section className="mb-16">
          <h2 className="text-3xl font-bold text-blue-600 mb-6">Как нас найти</h2>
          <div className="aspect-w-16 aspect-h-9">
            <iframe
              src="https://yandex.ru/map-widget/v1/?ll=54.804886%2C55.428327&z=17&l=map"
              width="100%"
              height="450"
              frameBorder="0"
              allowFullScreen="true"
              className="rounded-lg shadow-md"
              title="Карта расположения Дуслык"
            ></iframe>
          </div>
        </section>
      </main>

      {/* Footer */}
      <footer className="bg-blue-600 text-white py-8">
        <div className="container mx-auto px-4 text-center">
          <p>&copy; 2024 Дуслык. Все права защищены.</p>
        </div>
      </footer>
    </div>
  );
}

export default App;
